/**
 * Helpers for the Policystream "Download Client Data" flow. Kept free of React
 * so that they can be unit tested directly.
 */

export interface ClientDataCsvInfo {
  url: string;
  filename: string;
  rowCount: number | null;
  expiresAt: string | null;
  failedCount: number;
  failedQuoteIds: Array<string>;
}

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** Whether the value (from an <input type='date'>) is a real YYYY-MM-DD date. */
export function isValidIsoDate(value: string): boolean {
  if (!ISO_DATE_PATTERN.test(value)) {
    return false;
  }
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

/**
 * Validate the (optional) date range. Returns an error message, or null if the
 * range is valid. Both dates are optional; empty strings mean "no bound".
 */
export function getDateRangeError(
  startDate: string,
  endDate: string,
): string | null {
  if (startDate && !isValidIsoDate(startDate)) {
    return 'Please enter a valid start date.';
  }
  if (endDate && !isValidIsoDate(endDate)) {
    return 'Please enter a valid end date.';
  }
  // YYYY-MM-DD strings compare correctly as strings
  if (startDate && endDate && startDate > endDate) {
    return 'The start date must be on or before the end date.';
  }
  return null;
}

/**
 * Pull the CSV link out of the task pool's additionalInformation (a JSON
 * string, or already-parsed object depending on the Apollo cache).
 */
export function parseClientDataCsvInfo(
  additionalInformation: unknown,
): ClientDataCsvInfo | null {
  let parsed: unknown = additionalInformation;
  if (typeof additionalInformation === 'string') {
    if (!additionalInformation) {
      return null;
    }
    try {
      parsed = JSON.parse(additionalInformation);
    } catch (e) {
      return null;
    }
  }
  if (!isRecord(parsed)) {
    return null;
  }
  const info = parsed.client_data_csv;
  if (!isRecord(info)) {
    return null;
  }
  const {
    url,
    filename,
    row_count,
    expires_at,
    failed_count,
    failed_quote_ids,
  } = info;
  if (typeof url !== 'string' || !url) {
    return null;
  }
  return {
    url,
    filename:
      typeof filename === 'string' && filename ? filename : 'client-data.csv',
    rowCount: typeof row_count === 'number' ? row_count : null,
    expiresAt: typeof expires_at === 'string' ? expires_at : null,
    failedCount: typeof failed_count === 'number' ? failed_count : 0,
    failedQuoteIds: Array.isArray(failed_quote_ids)
      ? failed_quote_ids.filter(
          (quoteId): quoteId is string => typeof quoteId === 'string',
        )
      : [],
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Trigger a browser download of the file at the (presigned) url. */
export function triggerFileDownload(url: string, filename: string) {
  const link = document.createElement('a');
  link.href = url;
  // NB the download attribute is ignored for cross-origin urls; the S3 object
  // is uploaded with Content-Disposition: attachment so it downloads anyway
  link.download = filename;
  link.rel = 'noopener';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
